import { FC, forwardRef, useEffect, useRef, useState } from 'react';

interface ISelectorProps {
  values: any[];
  value?: any;
  onSelect?: (value: any) => void;
  onChange?: (value: any) => void;
  selectorClassname?: string;
  optionsClassname?: string;
  chevronDownClassname?: string;
  formatValue?: (val: string) => string;
  placeholder?: string;
  showEmptySelect?: boolean;
  scrollBottom?: boolean;
  ref?: any;
}

/**
 * @param props - The props for the component.
 * @param placeholder - placeholder for the input.
 * @param selectorClassname - custom class name for the select container.
 * @param optionsClassname - custom class name for the select option.
 * @param chevronDownClassname - custom class name for the select chevron.
 * @param onSelect - Function which is called after selecting value
 * @param onChange - Function which is called after selecting value
 * @param formatValue - Function to format option value
 * @param showEmptySelect - Indicates to show empty select
 * @param ref - ref for the input.
 * @param values - values for select.
 * @param value - value for select.
 * @returns A React component that displays an selector.
 */

const Selector: FC<ISelectorProps> = forwardRef((props, ref) => {
  const {
    onSelect,
    values,
    value,
    selectorClassname,
    optionsClassname,
    chevronDownClassname,
    placeholder = 'Select',
    formatValue,
    onChange,
    showEmptySelect = true,
    scrollBottom = false,
    ...rest
  } = props;

  const [opened, setOpened] = useState(false);
  const focusRef = useRef<any>(null);
  const selectorRef = useRef<any>(null);
  const optionsRef = useRef<any>(null);

  useEffect(() => {
    if (scrollBottom && optionsRef.current) {
      optionsRef.current.scrollTop = optionsRef.current.scrollHeight;
    }
  }, [scrollBottom, opened]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (opened) {
      focusRef.current.focus();
    }
  }, [opened, ref]);

  const onOptionClick = (option: any) => {
    setOpened(false);
    if (onSelect) {
      onSelect(option);
    }
    if (onChange) {
      onChange(option);
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      setOpened(false);
    }
  };
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
      data-opened={opened}
      ref={selectorRef}
    >
      <select
        value={value ?? ''}
        style={{ display: 'none' }}
        {...rest}
        onChange={() => console.log('CHANGED')}
        ref={ref as any}
      >
        {values.map((value, index) => (
          <option key={`${value}_${index}`} value={value}>
            {value}
          </option>
        ))}
      </select>
      <div
        className={selectorClassname}
        onClick={() => setOpened((prev) => !prev)}
        ref={focusRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {!value && <span>{placeholder}</span>}
        <p>{formatValue ? formatValue(value) : value}</p>
        <span className={chevronDownClassname} />
      </div>
      {opened && (
        <div className={optionsClassname} ref={optionsRef}>
          {value === undefined && showEmptySelect && (
            <div onClick={() => onOptionClick(undefined)} data-selected>
              Select
              <span />
            </div>
          )}

          {values.map((optionValue, index) => (
            <div
              key={`${optionValue}_${index}`}
              onClick={() => onOptionClick(optionValue)}
              data-selected={optionValue == value}
            >
              {optionValue}
              <span />
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export { Selector };
